import { zodResolver } from '@hookform/resolvers/zod';
import {
  Divider,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { LP_MANUAL_ACTION_LIST } from 'adminConstants/routes';
import Button from 'components/Button';
import ErrorBoundary from 'components/ErrorBoundary';
import AllocationsManager, {
  type Allocation,
} from 'components/new/AllocationsManager';
import Page from 'components/new/Page';
import PageLoader from 'components/PageLoader';
import { useLayoutContext } from 'contexts/LayoutContext';
import { AllocationRule } from 'further-types/lp/allocations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import { CalendarDay } from 'further-ui/utils';
import { manualJournal as rules } from 'further-ui/validations';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import { round, sumBy } from 'lodash';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { z } from 'zod';

import Transaction from './Transaction';

const formSchema = rules.CreateManualAction;

export type FormValues = z.infer<typeof formSchema>;

const emptyTransaction = {
  transactionTypeId: '',
  debitAmountLcy: 0,
  debitAmountTcy: 0,
  creditAmountLcy: 0,
  creditAmountTcy: 0,
};

type Props = {
  vehicleId: string;
};

const AddEditManualAction: React.FC<Props> = ({ vehicleId }) => {
  const { vehicle } = useLayoutContext();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { control, watch, formState, setValue, handleSubmit } =
    useForm<FormValues>({
      shouldFocusError: true,
      resolver: zodResolver(formSchema),
      defaultValues: {
        description: '',
        ledgerDate: new CalendarDay(new Date()),
        allocations: [],
        transactions: [emptyTransaction, emptyTransaction],
        allocationRule: AllocationRule.ByCommitmentAndClosingDate,
        totalAmountTcy: 0,
        totalAmountLcy: 0,
      },
      criteriaMode: 'all',
      resetOptions: {
        keepDefaultValues: false,
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transactions',
  });

  const handleAddTransaction = () => {
    append(emptyTransaction);
  };

  const handleCancel = () => {
    history.push(LP_MANUAL_ACTION_LIST);
  };

  const handleAllocationRuleChange = (allocationRule: AllocationRule) => {
    setValue('allocationRule', allocationRule);
  };
  const handleAllocationsChange = (allocations: Array<Allocation>) => {
    setValue('allocations', allocations);
  };

  const onSubmit = (data: FormValues) => {
    //eslint-disable-next-line no-console
    console.log(data);
    //TODO: Implement the API call to create the manual action
  };

  const formValues = watch();

  const setTotalAmounts = () => {
    const totalAmountLcy = sumBy(
      formValues.transactions,
      (transaction) => transaction.debitAmountLcy,
    );
    const totalAmountTcy = sumBy(
      formValues.transactions,
      (transaction) => transaction.debitAmountTcy,
    );
    setValue('totalAmountLcy', round(totalAmountLcy, 2));
    setValue('totalAmountTcy', round(totalAmountTcy, 2));
  };

  const handleTransactionTypeChange = (index: number, value: string) => {
    setValue(`transactions.${index}.transactionTypeId`, value);
    setTotalAmounts();
  };

  const handleDebitAmountChange = (index: number, value: number) => {
    setValue(`transactions.${index}.debitAmountLcy`, value);
    setValue(`transactions.${index}.debitAmountTcy`, value);
    setValue(`transactions.${index}.creditAmountLcy`, 0);
    setValue(`transactions.${index}.creditAmountTcy`, 0);
    setTotalAmounts();
  };

  const handleCreditAmountChange = (index: number, value: number) => {
    setValue(`transactions.${index}.creditAmountLcy`, value);
    setValue(`transactions.${index}.creditAmountTcy`, value);
    setValue(`transactions.${index}.debitAmountLcy`, 0);
    setValue(`transactions.${index}.debitAmountTcy`, 0);
    setTotalAmounts();
  };

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  const transactionsError = formState.errors.transactions?.message;

  return (
    <Page
      spacing="lg"
      heading="Add manual action"
      withHeadingDivider
      goBackAction={LP_MANUAL_ACTION_LIST}
      actionBar={
        <Row fullWidth centered justify="space-between">
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            Save
          </Button>
        </Row>
      }
    >
      <form>
        <Column spacing="lg" fullWidth>
          <FormField label="Date" size="md">
            <Controller
              name="ledgerDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  fullWidth
                  {...field}
                  value={field.value}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <FormField label="Description" size="md">
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <Divider />
          <Typography variant="h5">Transactions</Typography>
          <Grid container spacing={2}>
            <Grid size="grow">
              <Typography variant="h6" color="text.secondary">
                Transaction type
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="h6" color="text.secondary">
                Debit
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="h6" color="text.secondary">
                Credit
              </Typography>
            </Grid>
            <Grid size={1}></Grid>
          </Grid>
          {fields.map((field, index) => (
            <Transaction
              key={field.id}
              index={index}
              transaction={formValues.transactions[index]}
              transactionErrors={formState.errors.transactions?.[index]}
              transactionTypeOptions={transactionTypeOptions.filter(
                (option) => option.value !== null,
              )}
              currency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
              onTransctionTypeChange={handleTransactionTypeChange}
              onDebitAmountChange={handleDebitAmountChange}
              onCreditAmountChange={handleCreditAmountChange}
              onRemove={(index) => {
                remove(index);
              }}
            />
          ))}
          {transactionsError ? (
            <FormHelperText error>{transactionsError}</FormHelperText>
          ) : null}
          <div>
            <Button
              variant="outlined"
              size="large"
              onClick={handleAddTransaction}
            >
              Add another transaction
            </Button>
          </div>
          <Divider />
          <ErrorBoundary>
            <AllocationsManager
              vehicleId={vehicleId}
              allocations={formValues.allocations}
              allocatedActionLabel="Manual action"
              allocatedAmountTcy={formValues.totalAmountTcy}
              currency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
              baseCurrency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
              allocationRule={formValues.allocationRule}
              onAllocationRuleChange={handleAllocationRuleChange}
              onAllocationsChange={handleAllocationsChange}
              error={formState.errors.allocations?.message}
              currentActionIds={id ? [id] : undefined}
              date={formValues.ledgerDate}
            />
          </ErrorBoundary>
        </Column>
      </form>
    </Page>
  );
};

const AddEditManualActionWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <AddEditManualAction vehicleId={selectedVehicleId} />;
};

export default AddEditManualActionWrapper;
